'use client';

import Link from 'next/link';
import { usePathname, useRouter } from "next/navigation";
import { twMerge } from 'tailwind-merge';
import ThemedImage from './ThemedImage';
import { FormEvent, HTMLAttributes, useEffect, useRef, useState } from 'react';
import { ILocalizedParams } from '../../models/ICommon';
import { useTheme } from 'next-themes';
import BurgerMenu from './svg/BurgerMenu';
import Close from './svg/Close';
import Crescent from './svg/Crescent';
import Moon from './svg/Moon';
import Search from './svg/Search';
import { IMenuItem } from '../../models/IMenuItem';
import { socialIcons, socialsTypeMap } from './svg/socials/SocialsIcons';
import { ImageProps } from 'next/image';
import LeftArrow from './svg/LeftArrow';
import { IGeneralSettings } from '../../models/IGeneralSettings';

interface Props {
  locale: string;
  menu: IMenuItem[];
}

interface SearchingProps {
  input: string;
  isSearching: boolean;
}

export default function Navbar({ locale, menu, settings }: ILocalizedParams<{ menu: IMenuItem[]; settings: IGeneralSettings }>) {
  const [searchingState, setSearchingState] = useState<SearchingProps>({isSearching: false, input: ''});
  const router = useRouter();
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!searchingState.input) return;
    setSearchingState({input: '', isSearching: false});
    router.push(`/search?q=${searchingState.input}`);
  }

  const ref = useRef<any>();

  const escHandler = (event: KeyboardEvent) => {
    const {key} = event;
      if (key === "Escape") setSearchingState({isSearching: false, input: ''});
  }

  const clickHandler = (event: MouseEvent) => {
    const {target} = event;
      if (ref.current && !ref.current.contains(target)) setSearchingState({isSearching: false, input: ''});
  }

  useEffect(() => {
    document.addEventListener('keydown', escHandler);
    document.addEventListener('mousedown', clickHandler);
    return () => {
      document.removeEventListener('keydown', escHandler);
      document.removeEventListener('mousedown', clickHandler);
    }
  },[])
  
  return (
    <form onSubmit={onSubmit} ref={ref} className='sticky top-0 w-full z-20 navbar overflow-y-visible h-fit'>
      <DesktopNavbar locale={locale} menu={menu} settings={settings} searching={{state: searchingState, setState: setSearchingState}}/>
      <MobileNavbar locale={locale} menu={menu} settings={settings} searching={{state: searchingState, setState: setSearchingState}}/>
    </form>
  )
}

const isSelected = (href: string, pathname: string) => {
  const strippedPath = pathname.split('?')[0].toLowerCase();  
  const isPost = strippedPath.split('/').filter(x =>x).length >= 2;
  if (isPost) return strippedPath.includes(href.toLowerCase());
  else return strippedPath.endsWith(`/${href?.toLowerCase()}`);
}


function Utils({ locale, className, searching }: ILocalizedParams<{className?: string, searching: {state: SearchingProps; setState: Function}}>) {
  const pathname = usePathname();
  const isSearchPage = pathname.split('?')[0].endsWith('/search');

  return (
    <div className={twMerge('flex items-center gap-x-[24px] [&>*]:cursor-pointer flex-shrink-0 justify-end [&>*]:w-[36px] w-[120px]', searching.state.isSearching && 'w-fit', className)}>
      {searching.state.isSearching ? 
      <Link hrefLang={locale} href={`/search?q=${searching.state.input}`} className=' grid place-items-center' onClick={() => searching.setState(prevState => ({...prevState, isSearching: !prevState.isSearching}))}>
      <Search className='w-[30px] h-[24px]' />
    </Link>
      :  (
        <>
        <ThemeSwitcher />
      <div className=' grid place-items-center' onClick={() => isSearchPage ? undefined : searching.setState(prevState => ({...prevState, isSearching: true}))}>
        <Search className='w-[30px] h-[24px]' />
      </div>
        </>
      )}
    </div>
  )
}

function Logo({ locale, settings, ...props }: { locale: string } & Partial<ImageProps> & { settings: IGeneralSettings }) {
  return (
    <div className='w-fit h-fit grid place-items-center'>
    <a hrefLang={locale} title={'logo'} href={`/`} className={twMerge('w-[122.5px] h-[36px] flex-shrink-0 noindex')}>
      <ThemedImage fill sizes='100vw' eager src={settings.publisherSettings.logo || settings.publisherSettings.fallbackLogo} darkSrc={settings.publisherSettings.logoDarkmode || settings.publisherSettings.fallbackLogo} alt='foochia-logo' className={twMerge('h-full w-full', props.className)} />
    </a>
    </div>
  )
}

function DesktopNavbar({ locale, menu, settings, searching }: ILocalizedParams<{ menu: IMenuItem[]; settings: IGeneralSettings; searching: {state: SearchingProps; setState: Function} }>) {

  return (
    <>

      <div className='flex-shrink-0 w-full hidden desktop:flex bg-primary-100 dark:bg-night-100 border-solid border-b-[2px] border-b-secondary-400 px-[12px] h-[71px]'>
        <div className='flex items-center justify-between desktop:px-0 max-w-[1296px] mx-auto w-full'>

      {searching.state.isSearching ? (
        <div className='flex items-center bg-transparent w-full gap-x-[16px]'>
          <Close onClick={() => searching.setState(prevState => ({...prevState, isSearching: false}))} className='flex-shrink-0 w-[36px] h-[36px]'/>
          <input
          value={searching.state.input}
          onChange={({target: {value}}) => searching.setState(prevState => ({...prevState, input: value}))}
          autoFocus
          placeholder='ابحث هنا'
          className='h-full w-full bg-transparent border-none outline-none text-[20px]'
          />
          <input className='hidden z-[-100]' type='submit'  />
          </div>
      ):  (
        <>
        <Logo locale={locale} settings={settings} />
        <DesktopMenu locale={locale} menu={menu} />
        </>
      )}
      <Utils locale={locale} searching={searching} />
      </div>
      </div>
    </>
  )
}


function MobileNavbar({ locale, menu, settings, searching }: ILocalizedParams<{ menu: IMenuItem[]; settings: IGeneralSettings; searching: {state: SearchingProps; setState: Function} }>) {
  return (
    <div className='flex-shrink-0 flex desktop:hidden items-center justify-between bg-primary-100 dark:bg-night-100 border-solid border-b-[2px] border-b-secondary-400 w-full px-[16px] h-[71px]'>
      {searching.state.isSearching ? (
        <div className={twMerge('flex items-center bg-transparent w-full gap-x-[24px]', searching.state.isSearching && 'gap-x-[16px]')}>
          <Close onClick={() => searching.setState(prevState => ({...prevState, isSearching: false}))} className='flex-shrink-0 w-[36px] h-[36px]'/>
          <input
          value={searching.state.input}
          onChange={({target: {value}}) => searching.setState(prevState => ({...prevState, input: value}))}
          autoFocus
          placeholder='ابحث هنا'
          className='h-full w-full bg-transparent border-none outline-none h3'
          />
          <input className='hidden z-[-100]' type='submit'  />
          </div>
      ):  (

        <div className='flex items-center gap-x-[26px]'>
        <MobileMenu locale={locale} menu={menu} settings={settings} />
        <Logo locale={locale} settings={settings} />
      </div>
        )}
      <Utils locale={locale} searching={searching} />
    </div>
  )

}

function MobileMenu({ menu, locale, settings }: Props & { settings: IGeneralSettings }) {

  const [open, setOpen] = useState(false);

  const [expanded, setExpanded] = useState<number | undefined>();

  const toggleOpen = () => {
    const newState = !open;
    document.documentElement.style.overflowY = newState ? 'unset' : 'hidden';
    setOpen(newState);
    setExpanded(undefined);
  }

  const { push } = useRouter();
  const pathname = usePathname();

  const menuRef = useRef<any>();

  const handleClick = (e: MouseEvent) => {
    if (open && !menuRef.current?.contains(e.target)) toggleOpen();
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClick, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.documentElement.style.overflowY = 'unset';
    };
  });


  const icons = Object.values(socialIcons).filter(x => settings.socials.find(y => y.type === socialsTypeMap[x.type]));

  const Menu = () => (
    <div
      ref={menuRef}
      className={twMerge('overflow-y-auto z-[1000] py-[23px] px-[40px] bg-white dark:bg-night-100 fixed flex flex-col dark:left-0 left-[50%] translate-x-[-50%] dark:translate-x-0 border-solid border-b-[2px] border-b-secondary-400 w-full h-[calc(100%-71px)] top-[71px] justify-between')}
    >
      <div className='flex flex-col'>
        {menu?.sort((a, b) => a.weight - b.weight).map(({ id, title, destination, children }, i) => {
          const isLast = i === menu.length - 1;
          const expandable = children?.length;
          return (
            <div className={twMerge('flex flex-col')} key={id}>
              <div className='flex flex-col'>
                <div className={twMerge('flex items-center justify-between')}>
                  <span
                    onClick={() => {
                      push(`/${destination?.toLowerCase()}`);
                      toggleOpen();
                    }}
                    className={twMerge('h5 dark:text-white text-[20px] leading-[140%] cursor-pointer py-[12px]', isSelected(destination, pathname) && 'text-secondary-400 font-kufi-600')}
                  >
                    {title}
                  </span>
                  {expandable ? (
                    <LeftArrow
                      key={i}
                      onClick={() => setExpanded(expanded === i ? undefined : i)}
                      className={twMerge('h-[15px] w-[10px] [&>*]:fill-primary-200 dark:[&>*]:fill-white rotate-[270deg]', expanded === i && 'rotate-[90deg]')}
                    />
                  ) : null}
                </div>
                {isLast ? null : <div className='w-full h-[1px] bg-secondary-400 flex-shrink-0' />}
              </div>
              {expandable ? (
                <div className={twMerge('flex-col w-full px-[10px] h-fit gap-y-[10px] hidden', expanded === i && 'flex')}>
                  {children.map((x, j) => {
                    const isLastChild = children.length - 1 === j;
                    return (
                      <div className='flex flex-col' key={j}>
                        <span
                          onClick={() => {
                            push(`/${x.destination?.toLowerCase()}`);
                            toggleOpen();
                          }}
                          className={twMerge('dark:text-whit h3 cursor-pointer py-[10px]', isSelected(x.destination, pathname) && 'text-secondary-400 font-kufi-600')}
                        >
                          {x.title}
                        </span>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>

      <div className='grid grid-cols-3 justify-between'>
        {icons.map(({ Component, type, href }) => (
          <div className='grid place-items-center' key={type}>
            <Link rel='noopener noreferrer' hrefLang={locale} title={type} target='_blank' href={settings.socials.find(s => s.type === socialsTypeMap[type])?.url || ''}>
              <Component className='w-[48px] h-[48px] dark:[&>path]:!fill-white' />
            </Link>
          </div>
        ))}

      </div>
    </div>
  )


  return (
    <>
      {open ? <Menu /> : null}
      <div
        className='[&>*]:fill-secondary-400 dark:[&>*]:fill-white cursor-pointer w-fit h-fit [&>*]:h-[36px] [&>*]:w-[36px]'
        onClick={() => toggleOpen()}
      >
        {open ? <Close /> : <BurgerMenu />}
      </div>
    </>
  )
}


function DesktopMenu({ menu, locale }: Props) {

  const pathname = usePathname();
  return (
    <div className='flex items-center h-full pt-[14px] w-[1296px] overflow-visible gap-x-[48px] pr-[48px]'>
      {menu?.sort((a, b) => a.weight - b.weight).map(({ id, destination, title, children }, i) => {
        return (
          <div
            className={twMerge('grid place-items-center relative h-full whitespace-nowrap group overflow-visible')}
            key={id}
          >
            <Link
              hrefLang={locale}
              title={title!}
              className={twMerge('h-full w-full grid place-items-center')}
              href={`/${destination?.toLowerCase()}`}
            >
              <span className={twMerge('h5 dark:text-primary-100 sm desktop:md', isSelected(destination, pathname) && '!text-secondary-400 font-kufi-600')}>{title}</span>
              {isSelected(destination, pathname) ? <div className='w-full absolute bottom-0 left-0 bg-secondary-300 dark:bg-white rounded-tr-full rounded-tl-full self-end h-[8px]' /> : null}

            </Link>
            {children?.length ?
              <div className={twMerge(`max-h-[400px] overflow-y-auto z-[10] grid-cols-1 p-[8px] rounded-br-[12px] rounded-bl-[11.90px] bg-primary-100 dark:bg-night-100 overflow-visible gap-[20px] absolute top-full left-[50%] translate-x-[-50%] hidden w-max max-w-[400px] h-fit group-hover:grid`)}>
                {children.map((x, i) => <Link hrefLang={locale} title={x.destination} key={x.id} href={`/${x.destination.toLowerCase()}`} className={twMerge(`col-span-1 p-[10px] bg-transparent px-[16px] py-[5px]`,)}><span className={twMerge('h5 whitespace-pre-wrap sm text-primary-300 dark:text-white ',isSelected(x.destination.toLowerCase(), pathname) && '!text-secondary-400')}>{x.title}</span></Link>)}
              </div> : null}
          </div>
        )
      })}
    </div>
  )
}



function ThemeSwitcher(props: Partial<HTMLAttributes<HTMLElement>>) {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div {...props} className={twMerge('grid place-items-center [&>*]:!flex-shrink-0', props?.className)} onClick={() => setTheme(isDark ? 'light' : 'dark')}>
      {isDark ? <Moon className='h-[30px] w-[30px]' /> : <Crescent className='w-[18px] h-[20px]' />}
    </div>

  )
}

